import { useState, useEffect, useCallback } from "react";
import {
  ShoppingBagIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  PhotoIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ChartBarIcon,
  CubeIcon,
  SwatchIcon,
  TagIcon,
  ArrowUpTrayIcon,
  CurrencyDollarIcon,
  LinkIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { useStudioUser } from "../hooks/useStudioUser";
import { useStudioPlan } from "../hooks/useStudioPlan";
import Notification from "../components/Notification";
import UpgradePrompt from "../components/UpgradePrompt";
import { GatedPageLayout } from "../components/PlanGatedPage";
import SellToStudentModal from "../components/store/SellToStudentModal";
import StorePaymentHistoryModal from "../components/store/StorePaymentHistoryModal";

// Types
interface ProductVariation {
  id: string;
  size?: string;
  color?: string;
  sku: string;
  price: number;
  inventory: number;
  lowStockThreshold: number;
}

interface Product {
  id: string;
  name: string;
  description: string;
  category: string;
  images: string[]; // Base64 encoded images
  variations: ProductVariation[];
  isActive: boolean;
  created_at?: string;
  updated_at?: string;
}

// Security constants
const MAX_LENGTHS: Record<string, number> = {
  productName: 200,
  description: 1000,
  category: 100,
  sku: 50,
  size: 50,
  color: 50,
};

const MAX_IMAGES = 5;
const MAX_VARIATIONS = 20;
const SUBMISSION_COOLDOWN = 2000;

// Input sanitization
const sanitizeInput = (input: string, fieldType?: string): string => {
  if (typeof input !== 'string') return '';
  let sanitized = input;
  sanitized = sanitized.replace(/[<>]/g, '');
  sanitized = sanitized.replace(/javascript:/gi, '');
  sanitized = sanitized.replace(/vbscript:/gi, '');
  sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  sanitized = sanitized.replace(/data:text\/html/gi, '');
  return sanitized;
};

export default function Store() {
  const { session, getAuthToken } = useAuth();
  const { studioUser, loading: studioUserLoading } = useStudioUser();
  const { hasCapability, loading: planLoading } = useStudioPlan();

  // State
  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showInventoryDashboard, setShowInventoryDashboard] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [sellProduct, setSellProduct] = useState<Product | null>(null);
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);
  
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    title?: string;
  }>({
    show: false,
    message: "",
  });

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    isActive: true,
  });

  const [productImages, setProductImages] = useState<string[]>([]);
  const [variations, setVariations] = useState<ProductVariation[]>([
    {
      id: crypto.randomUUID(),
      size: "",
      color: "",
      sku: "",
      price: 0,
      inventory: 0,
      lowStockThreshold: 5,
    },
  ]);

  // Price input state for better UX (allows natural typing)
  const [priceInputs, setPriceInputs] = useState<Record<string, string>>({});
  // Inventory input state for better UX (allows natural typing)
  const [inventoryInputs, setInventoryInputs] = useState<Record<string, string>>({});

  // Categories (can be expanded)
  const categories = [
    "Uniforms",
    "Belts",
    "Sparring Gear",
    "Training Equipment",
    "Accessories",
    "Apparel",
    "Other",
  ];

  // Fetch products. `silent` skips the loading state so a refresh (e.g. after a
  // sale) updates stock numbers in place without flashing the spinner.
  const fetchProducts = useCallback(async (options?: { silent?: boolean }) => {
      const authToken = getAuthToken();
      if (!studioUser?.studio_id || !authToken) {
        return;
      }

      if (!options?.silent) setProductsLoading(true);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/products/${studioUser.studio_id}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch products');
        }

        const data = await response.json();
        
        // Transform data to match frontend interface
        const transformedProducts = data.map((product: any) => ({
          id: product.id,
          name: product.name,
          description: product.description,
          category: product.category,
          images: product.images || [],
          variations: product.variations.map((v: any) => ({
            id: v.id,
            size: v.size,
            color: v.color,
            sku: v.sku,
            price: parseFloat(v.price),
            inventory: v.inventory,
            lowStockThreshold: v.low_stock_threshold,
          })),
          isActive: product.is_active,
          created_at: product.created_at,
          updated_at: product.updated_at,
        }));

        setProducts(transformedProducts);
      } catch (error) {
        console.error('Error fetching products:', error);
        setNotification({
          show: true,
          message: 'Failed to load products',
          title: 'Error',
        });
      } finally {
        if (!options?.silent) setProductsLoading(false);
      }
  }, [studioUser?.studio_id, getAuthToken]);

  // Fetch products on mount
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Image compression function (matches Events page limits to prevent database bloat)
  const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      const img = new Image();
      
      img.onload = () => {
        // More aggressive compression - smaller max size to prevent database bloat
        const maxSize = 400; // Same as Events page
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        // More aggressive quality reduction (60% instead of 70%) to match Events
        const compressedBase64 = canvas.toDataURL('image/jpeg', 0.6);
        resolve(compressedBase64);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  // Handle image upload
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (productImages.length + files.length > MAX_IMAGES) {
      setNotification({
        show: true,
        message: `You can only upload up to ${MAX_IMAGES} images per product`,
        title: "Image Limit Reached",
      });
      return;
    }

    const newImages: string[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Check file type
      if (!file.type.match(/image\/(jpeg|jpg|png|webp)/)) {
        setNotification({
          show: true,
          message: "Please select JPEG, PNG, or WebP files only",
          title: "Invalid File Type",
        });
        continue;
      }

      // Check file size (1MB = 1,048,576 bytes) - same limit as Events
      if (file.size > 1048576) {
        setNotification({
          show: true,
          message: "File size must be less than 1MB",
          title: "File Too Large",
        });
        continue;
      }

      try {
        const compressedBase64 = await compressImage(file);
        newImages.push(compressedBase64);
      } catch (error) {
        setNotification({
          show: true,
          message: "Failed to process image. Please try again.",
          title: "Image Processing Error",
        });
      }
    }

    setProductImages([...productImages, ...newImages]);
  };

  // Remove image
  const removeImage = (index: number) => {
    setProductImages(productImages.filter((_, i) => i !== index));
  };

  // Add variation
  const addVariation = () => {
    if (variations.length >= MAX_VARIATIONS) {
      setNotification({
        show: true,
        message: `Maximum ${MAX_VARIATIONS} variations allowed per product`,
        title: "Variation Limit Reached",
      });
      return;
    }

    const newVariationId = crypto.randomUUID();
    setVariations([
      ...variations,
      {
        id: newVariationId,
        size: "",
        color: "",
        sku: "",
        price: 0,
        inventory: 0,
        lowStockThreshold: 5,
      },
    ]);
    // Initialize empty price and inventory inputs for new variation
    setPriceInputs((prev) => ({ ...prev, [newVariationId]: '' }));
    setInventoryInputs((prev) => ({ ...prev, [newVariationId]: '' }));
  };

  // Remove variation
  const removeVariation = (id: string) => {
    if (variations.length === 1) {
      setNotification({
        show: true,
        message: "At least one variation is required",
        title: "Cannot Remove",
      });
      return;
    }
    setVariations(variations.filter((v) => v.id !== id));
    // Remove price and inventory inputs for deleted variation
    setPriceInputs((prev) => {
      const newInputs = { ...prev };
      delete newInputs[id];
      return newInputs;
    });
    setInventoryInputs((prev) => {
      const newInputs = { ...prev };
      delete newInputs[id];
      return newInputs;
    });
  };

  // Generate SKU from product name and variation details
  // When size and color are both empty, appends a unique suffix from variationId to avoid collisions
  // when multiple products share the same name (e.g., two "Flow Jiu Jitsu" products)
  const generateSKU = (productName: string, size?: string, color?: string, variationId?: string): string => {
    // Clean product name: remove special chars, convert to uppercase, take first 3-4 words
    const nameParts = productName
      .toUpperCase()
      .replace(/[^A-Z0-9\s]/g, '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 3)
      .map(word => word.substring(0, 4))
      .join('');
    
    // Get size abbreviation (first 2-3 chars)
    const sizeAbbr = size ? size.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 3) : '';
    
    // Get color abbreviation (first 2-3 chars)
    const colorAbbr = color ? color.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 3) : '';
    
    // Combine: NAME-SIZE-COLOR (e.g., "MARTIALARTS-MED-WHT")
    let parts = [nameParts, sizeAbbr, colorAbbr].filter(Boolean);
    let sku = parts.join('-') || 'PROD-' + Date.now().toString().slice(-6);

    // When size and color are both empty, append unique suffix to prevent collisions
    // when different products share the same name (e.g., "Flow Jiu Jitsu" x2)
    if (!sizeAbbr && !colorAbbr && nameParts && variationId) {
      const uniqueSuffix = variationId.replace(/-/g, '').slice(-6).toUpperCase();
      sku = `${nameParts}-${uniqueSuffix}`;
    }

    return sku;
  };

  // Update variation
  const updateVariation = (id: string, field: keyof ProductVariation, value: any) => {
    const updatedVariations = variations.map((v) => {
      if (v.id === id) {
        const updated = { ...v, [field]: value };
        // Auto-generate SKU when size or color changes
        if (field === 'size' || field === 'color') {
          updated.sku = generateSKU(formData.name, updated.size, updated.color, updated.id);
        }
        return updated;
      }
      return v;
    });
    setVariations(updatedVariations);
  };

  // Auto-generate SKUs when product name changes
  useEffect(() => {
    if (formData.name && showModal) {
      setVariations(prevVariations => prevVariations.map((v) => ({
        ...v,
        sku: generateSKU(formData.name, v.size, v.color, v.id)
      })));
    }
  }, [formData.name, showModal]);

  // Handle price input change (allows natural typing)
  const handlePriceChange = (variationId: string, value: string) => {
    // Allow empty string, numbers, and one decimal point
    const cleaned = value.replace(/[^\d.]/g, '');
    
    // Ensure only one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      return; // Invalid input, don't update
    }
    
    // Limit to 2 decimal places
    if (parts.length === 2 && parts[1].length > 2) {
      return; // Too many decimal places, don't update
    }
    
    // Update the text input state
    setPriceInputs((prev) => ({ ...prev, [variationId]: cleaned }));
    
    // Update the actual price value (convert to number)
    const numValue = cleaned === '' || cleaned === '.' ? 0 : parseFloat(cleaned);
    updateVariation(variationId, 'price', isNaN(numValue) ? 0 : numValue);
  };

  // Handle inventory input change (allows natural typing)
  const handleInventoryChange = (variationId: string, value: string) => {
    // Allow only digits
    const cleaned = value.replace(/[^\d]/g, '');
    
    // Update the text input state
    setInventoryInputs((prev) => ({ ...prev, [variationId]: cleaned }));
    
    // Update the actual inventory value (convert to integer)
    const numValue = cleaned === '' ? 0 : parseInt(cleaned);
    updateVariation(variationId, 'inventory', isNaN(numValue) ? 0 : numValue);
  };

  // Open modal for new product
  const openCreateModal = () => {
    setEditingProduct(null);
    setFormData({
      name: "",
      description: "",
      category: "",
      isActive: true,
    });
    setProductImages([]);
    const newVariationId = crypto.randomUUID();
    const initialVariation = {
      id: newVariationId,
      size: "",
      color: "",
      sku: "",
      price: 0,
      inventory: 0,
      lowStockThreshold: 5,
    };
    setVariations([initialVariation]);
    setPriceInputs({ [newVariationId]: '' });
    setInventoryInputs({ [newVariationId]: '' });
    setShowModal(true);
  };

  // Open modal for editing
  const copyPaymentLink = async (product: Product) => {
    const url = `${window.location.origin}/buy/${product.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setNotification({
        show: true,
        title: 'Payment link copied',
        message: `Anyone with this link can pay for ${product.name}: ${url}`,
      });
    } catch {
      setNotification({
        show: true,
        title: 'Payment link',
        message: `Copy this link: ${url}`,
      });
    }
  };

  const openEditModal = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      category: product.category,
      isActive: product.isActive,
    });
    setProductImages(product.images);
    setVariations(product.variations);
    // Initialize price and inventory inputs for existing variations
    const initialPriceInputs: Record<string, string> = {};
    const initialInventoryInputs: Record<string, string> = {};
    product.variations.forEach((v) => {
      initialPriceInputs[v.id] = v.price > 0 ? v.price.toString() : '';
      initialInventoryInputs[v.id] = v.inventory > 0 ? v.inventory.toString() : '';
    });
    setPriceInputs(initialPriceInputs);
    setInventoryInputs(initialInventoryInputs);
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setEditingProduct(null);
  };

  // Save product
  const handleSaveProduct = async (e: React.FormEvent) => {
    e.preventDefault();

    // Rate limiting
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before saving again",
        title: "Rate Limited",
      });
      return;
    }

    // Validation
    const sanitizedName = sanitizeInput(formData.name);
    if (!sanitizedName || sanitizedName.length < 2) {
      setNotification({
        show: true,
        message: "Product name is required (minimum 2 characters)",
        title: "Validation Error",
      });
      return;
    }

    if (!formData.category) {
      setNotification({
        show: true,
        message: "Please select a category",
        title: "Validation Error",
      });
      return;
    }

    // Validate variations and ensure SKUs are generated
    const variationsWithSKUs = variations.map((variation) => {
      // SKU is auto-generated, so we just ensure it exists
      if (!variation.sku || variation.sku.trim() === '') {
        return {
          ...variation,
          sku: generateSKU(sanitizedName, variation.size, variation.color, variation.id)
        };
      }
      return variation;
    });

    // Validate price
    for (const variation of variationsWithSKUs) {
      if (variation.price <= 0) {
        setNotification({
          show: true,
          message: "All variations must have a valid price",
          title: "Validation Error",
        });
        return;
      }
    }

    // Check for duplicate SKUs within this product
    const skuCounts = new Map<string, number>();
    const duplicateSKUs: string[] = [];
    
    for (const variation of variationsWithSKUs) {
      const sku = variation.sku;
      const count = (skuCounts.get(sku) || 0) + 1;
      skuCounts.set(sku, count);
      
      if (count === 2) {
        duplicateSKUs.push(sku);
      }
    }

    if (duplicateSKUs.length > 0) {
      setNotification({
        show: true,
        message: `Duplicate variations found. Each combination of size and color must be unique. Duplicates: ${duplicateSKUs.join(', ')}`,
        title: "Duplicate Variations",
      });
      return;
    }

    setLastSubmissionTime(now);
    setSaving(true);

    const authToken = getAuthToken();
    if (!authToken) {
      setNotification({
        show: true,
        message: "Authentication required",
        title: "Error",
      });
      setSaving(false);
      return;
    }

    try {
      const payload = {
        name: sanitizedName,
        description: sanitizeInput(formData.description),
        category: formData.category,
        images: productImages,
        isActive: formData.isActive,
        variations: variationsWithSKUs.map((v) => ({
          sku: v.sku,
          size: v.size || null,
          color: v.color || null,
          price: v.price,
          inventory: v.inventory,
          lowStockThreshold: v.lowStockThreshold,
        })),
      };

      const url = editingProduct
        ? `${process.env.REACT_APP_BACKEND_URL}/api/supabase/products/${editingProduct.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/supabase/products`;

      const method = editingProduct ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save product');
      }

      const savedProduct = await response.json();

      // Transform response to match frontend interface
      const transformedProduct: Product = {
        id: savedProduct.id,
        name: savedProduct.name,
        description: savedProduct.description,
        category: savedProduct.category,
        images: savedProduct.images || [],
        variations: savedProduct.variations.map((v: any) => ({
          id: v.id,
          size: v.size,
          color: v.color,
          sku: v.sku,
          price: parseFloat(v.price),
          inventory: v.inventory,
          lowStockThreshold: v.low_stock_threshold,
        })),
        isActive: savedProduct.is_active,
        created_at: savedProduct.created_at,
        updated_at: savedProduct.updated_at,
      };

      if (editingProduct) {
        setProducts(products.map((p) => (p.id === editingProduct.id ? transformedProduct : p)));
        setNotification({
          show: true,
          message: "Product updated successfully",
          title: "Success",
        });
      } else {
        setProducts([transformedProduct, ...products]);
        setNotification({
          show: true,
          message: "Product created successfully",
          title: "Success",
        });
      }

      closeModal();
    } catch (error: any) {
      console.error('Error saving product:', error);
      setNotification({
        show: true,
        message: error.message || "Failed to save product. Please try again.",
        title: "Error",
      });
    } finally {
      setSaving(false);
    }
  };

  // Open delete confirmation modal
  const handleDeleteProduct = (productId: string) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      setProductToDelete(product);
      setShowDeleteModal(true);
    }
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setProductToDelete(null);
  };

  // Confirm and delete product
  const confirmDeleteProduct = async () => {
    if (!productToDelete) return;

    const authToken = getAuthToken();
    if (!authToken) {
      setNotification({
        show: true,
        message: "Authentication required",
        title: "Error",
      });
      closeDeleteModal();
      return;
    }

    setDeleting(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/products/${productToDelete.id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete product');
      }

      setProducts(products.filter((p) => p.id !== productToDelete.id));
      setNotification({
        show: true,
        message: "Product deleted successfully",
        title: "Success",
      });
      closeDeleteModal();
    } catch (error: any) {
      console.error('Error deleting product:', error);
      setNotification({
        show: true,
        message: error.message || "Failed to delete product. Please try again.",
        title: "Error",
      });
    } finally {
      setDeleting(false);
    }
  };

  // Filter products
  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === "all" || product.category === categoryFilter;
    const matchesStatus = statusFilter === "all" || 
      (statusFilter === "active" && product.isActive) ||
      (statusFilter === "inactive" && !product.isActive);
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Calculate inventory stats
  const inventoryStats = {
    totalProducts: products.length,
    totalVariations: products.reduce((sum, p) => sum + p.variations.length, 0),
    totalInventory: products.reduce(
      (sum, p) => sum + p.variations.reduce((vSum, v) => vSum + v.inventory, 0),
      0
    ),
    lowStock: products.reduce(
      (sum, p) => sum + p.variations.filter((v) => v.inventory <= v.lowStockThreshold).length,
      0
    ),
    outOfStock: products.reduce(
      (sum, p) => sum + p.variations.filter((v) => v.inventory === 0).length,
      0
    ),
  };

  if (planLoading) {
    return (
      <>
        <Notification
          show={notification.show}
          message={notification.message}
          title={notification.title}
          onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
        />
        <GatedPageLayout>
          <div className="flex justify-center py-16">
            <div className="animate-pulse flex space-x-4 items-center">
              <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin" />
              <div className="text-sm text-gray-600 dark:text-gray-400">Loading store...</div>
            </div>
          </div>
        </GatedPageLayout>
      </>
    );
  }

  if (!hasCapability('storePos')) {
    return (
      <>
        <Notification
          show={notification.show}
          message={notification.message}
          title={notification.title}
          onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
        />
        <GatedPageLayout
          title="Store"
          subtitle="Point-of-sale, inventory, and product sales for your studio"
        >
          <UpgradePrompt capability="storePos" hideTitle />
        </GatedPageLayout>
      </>
    );
  }

  return (
    <div className="relative bg-gray-50 dark:bg-gray-900">
      <div className="space-y-4 p-0 pt-6 xl:pt-0 xl:p-6">
        <Notification
          show={notification.show}
          message={notification.message}
          title={notification.title}
          onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
        />

        {/* Header */}
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Store
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage your studio store and products
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={() => setShowInventoryDashboard(!showInventoryDashboard)}
              className="inline-flex items-center justify-center rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
            >
              <ChartBarIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
              Inventory Dashboard
            </button>
            <button
              type="button"
              onClick={() => setShowPaymentHistory(true)}
              className="inline-flex items-center justify-center rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
            >
              <ClockIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
              Payment History
            </button>
            <button
              type="button"
              onClick={openCreateModal}
              className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
              Add Product
            </button>
          </div>
        </div>

        {/* Inventory Dashboard */}
        {showInventoryDashboard && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ShoppingBagIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />
                </div>
                <div className="ml-3 w-0 flex-1">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Total Products
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {inventoryStats.totalProducts}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <SwatchIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />
                </div>
                <div className="ml-3 w-0 flex-1">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Variations
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {inventoryStats.totalVariations}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CubeIcon className="h-6 w-6 text-green-400" aria-hidden="true" />
                </div>
                <div className="ml-3 w-0 flex-1">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Total Inventory
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {inventoryStats.totalInventory}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ExclamationCircleIcon className="h-6 w-6 text-yellow-400" aria-hidden="true" />
                </div>
                <div className="ml-3 w-0 flex-1">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Low Stock
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {inventoryStats.lowStock}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <XMarkIcon className="h-6 w-6 text-red-400" aria-hidden="true" />
                </div>
                <div className="ml-3 w-0 flex-1">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Out of Stock
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {inventoryStats.outOfStock}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow space-y-4 sm:space-y-0 sm:flex sm:items-center sm:gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon
                className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full rounded-md border-0 py-2 pl-10 pr-3 text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 bg-white dark:bg-gray-700"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="block w-full rounded-md border-0 py-2 pl-3 pr-10 text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6 bg-white dark:bg-gray-700"
            >
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="block w-full rounded-md border-0 py-2 pl-3 pr-10 text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6 bg-white dark:bg-gray-700"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Loading Indicator */}
        {(studioUserLoading || productsLoading) ? (
          <div className="flex justify-center items-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-200 dark:border-indigo-800 border-t-indigo-600 dark:border-t-indigo-400"></div>
              <p className="mt-4 text-sm font-medium text-gray-600 dark:text-gray-300">
                Loading products...
              </p>
            </div>
          </div>
        ) : products.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow">
                <ShoppingBagIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                  No products
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Get started by creating a new product.
                </p>
                <div className="mt-6">
                  <button
                    type="button"
                    onClick={openCreateModal}
                    className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                  >
                    <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                    Add Product
                  </button>
                </div>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow">
                <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                  No products match your filters
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Try adjusting your search or filter criteria.
                </p>
              </div>
            ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredProducts.map((product) => {
              const lowestPrice = Math.min(...product.variations.map((v) => v.price));
              const highestPrice = Math.max(...product.variations.map((v) => v.price));
              const totalInventory = product.variations.reduce((sum, v) => sum + v.inventory, 0);
              const hasLowStock = product.variations.some((v) => v.inventory <= v.lowStockThreshold && v.inventory > 0);
              const hasOutOfStock = product.variations.some((v) => v.inventory === 0);

              return (
                <div
                  key={product.id}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden hover:shadow-lg transition-shadow"
                >
                  {/* Product Image */}
                  <div className="aspect-w-1 aspect-h-1 w-full bg-gray-200 dark:bg-gray-700">
                    {product.images[0] ? (
                      <img
                        src={product.images[0]}
                        alt={product.name}
                        className="h-48 w-full object-cover object-center"
                      />
                    ) : (
                      <div className="h-48 w-full flex items-center justify-center">
                        <PhotoIcon className="h-12 w-12 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {product.name}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {product.category}
                        </p>
                      </div>
                      <div className="ml-2 flex-shrink-0 flex items-center space-x-1">
                        {!product.isActive && (
                          <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                            Inactive
                          </span>
                        )}
                        {hasOutOfStock && (
                          <span className="inline-flex items-center rounded-full bg-red-100 dark:bg-red-900/30 px-2 py-0.5 text-xs font-medium text-red-800 dark:text-red-400">
                            Out
                          </span>
                        )}
                        {hasLowStock && !hasOutOfStock && (
                          <span className="inline-flex items-center rounded-full bg-yellow-100 dark:bg-yellow-900/30 px-2 py-0.5 text-xs font-medium text-yellow-800 dark:text-yellow-400">
                            Low
                          </span>
                        )}
                      </div>
                    </div>

                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                      {product.description}
                    </p>

                    <div className="mt-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {lowestPrice === highestPrice
                            ? `$${lowestPrice.toFixed(2)}`
                            : `$${lowestPrice.toFixed(2)} - $${highestPrice.toFixed(2)}`}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {product.variations.length} variation{product.variations.length !== 1 ? 's' : ''} • {totalInventory} in stock
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 flex gap-2">
                      <button
                        onClick={() => setSellProduct(product)}
                        className="flex-1 inline-flex items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
                      >
                        <CurrencyDollarIcon className="h-4 w-4 mr-1" aria-hidden="true" />
                        Sell
                      </button>
                      {product.isActive && (
                        <button
                          onClick={() => copyPaymentLink(product)}
                          title="Copy payment link"
                          className="inline-flex items-center justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                          <LinkIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                      )}
                      <button
                        onClick={() => openEditModal(product)}
                        className="inline-flex items-center justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                      >
                        <PencilIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(product.id)}
                        className="inline-flex items-center justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-red-600 dark:text-red-400 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                      >
                        <TrashIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Product Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 shadow-xl transition-all w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                {/* Modal Header */}
                <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {editingProduct ? "Edit Product" : "Add New Product"}
                  </h3>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                {/* Modal Body */}
                <form onSubmit={handleSaveProduct} className="px-6 py-4 space-y-6">
                  {/* Basic Info */}
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="product-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Product Name *
                      </label>
                      <input
                        type="text"
                        id="product-name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        maxLength={MAX_LENGTHS.productName}
                        className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Description
                      </label>
                      <textarea
                        id="description"
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        maxLength={MAX_LENGTHS.description}
                        className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Category *
                        </label>
                        <select
                          id="category"
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          required
                        >
                          <option value="">Select a category</option>
                          {categories.map((cat) => (
                            <option key={cat} value={cat}>
                              {cat}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="flex items-center">
                        <input
                          id="is-active"
                          type="checkbox"
                          checked={formData.isActive}
                          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        <label htmlFor="is-active" className="ml-2 block text-sm text-gray-900 dark:text-white">
                          Active (visible to parents)
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Product Images */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Product Images (up to {MAX_IMAGES})
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                      {productImages.map((image, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={image}
                            alt={`Product ${index + 1}`}
                            className="h-32 w-full object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      {productImages.length < MAX_IMAGES && (
                        <label className="h-32 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-indigo-500 dark:hover:border-indigo-400 transition-colors">
                          <ArrowUpTrayIcon className="h-8 w-8 text-gray-400" />
                          <span className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            Upload
                          </span>
                          <input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            multiple
                            onChange={handleImageUpload}
                            className="sr-only"
                          />
                        </label>
                      )}
                    </div>
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      PNG, JPG, JPEG, WebP up to 1MB (will be compressed)
                    </p>
                  </div>

                  {/* Variations */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Product Variations *
                      </label>
                      <button
                        type="button"
                        onClick={addVariation}
                        className="inline-flex items-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                      >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        Add Variation
                      </button>
                    </div>
                    <div className="space-y-4">
                      {variations.map((variation, index) => (
                        <div
                          key={variation.id}
                          className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Variation {index + 1}
                            </span>
                            {variations.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeVariation(variation.id)}
                                className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                              >
                                <XMarkIcon className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                SKU (Auto-generated)
                              </label>
                              <input
                                type="text"
                                value={variation.sku || (formData.name ? generateSKU(formData.name, variation.size, variation.color, variation.id) : '')}
                                readOnly
                                disabled
                                className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm bg-gray-50 dark:bg-gray-600 text-sm text-gray-500 dark:text-gray-400 cursor-not-allowed"
                                placeholder={formData.name ? "Auto-generated from product name, size, and color" : "Enter product name first"}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                Size
                              </label>
                              <input
                                type="text"
                                value={variation.size}
                                onChange={(e) => updateVariation(variation.id, "size", e.target.value)}
                                maxLength={MAX_LENGTHS.size}
                                className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                placeholder="e.g., Medium, XL"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                Color
                              </label>
                              <input
                                type="text"
                                value={variation.color}
                                onChange={(e) => updateVariation(variation.id, "color", e.target.value)}
                                maxLength={MAX_LENGTHS.color}
                                className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                placeholder="e.g., White, Black"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                Price * ($)
                              </label>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={priceInputs[variation.id] || ''}
                                onChange={(e) => handlePriceChange(variation.id, e.target.value)}
                                placeholder="0.00"
                                className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                Inventory *
                              </label>
                              <input
                                type="text"
                                inputMode="numeric"
                                value={inventoryInputs[variation.id] || ''}
                                onChange={(e) => handleInventoryChange(variation.id, e.target.value)}
                                placeholder="0"
                                className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                Low Stock Alert
                              </label>
                              <input
                                type="number"
                                min="0"
                                value={variation.lowStockThreshold}
                                onChange={(e) => updateVariation(variation.id, "lowStockThreshold", parseInt(e.target.value) || 0)}
                                className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Modal Footer */}
                  <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="inline-flex justify-center rounded-md bg-white dark:bg-gray-700 px-4 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="inline-flex justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? "Saving..." : editingProduct ? "Update Product" : "Create Product"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && productToDelete && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={closeDeleteModal}
                    disabled={deleting}
                  >
                    <span className="sr-only pointer-events-none">Close</span>
                    <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                  </button>
                </div>
                <div>
                  <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                    Delete Product
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Are you sure you want to delete "{productToDelete.name}"? This action cannot be undone and will permanently delete:
                    </p>
                    <ul className="mt-3 space-y-1 text-sm text-gray-500 dark:text-gray-400">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>The product and all its information</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>All product variations ({productToDelete.variations.length})</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>All product images ({productToDelete.images.length})</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>All associated inventory data</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 flex flex-col xl:flex-row xl:justify-end space-y-3 xl:space-y-0 xl:space-x-3">
                  <button
                    type="button"
                    onClick={closeDeleteModal}
                    disabled={deleting}
                    className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="pointer-events-none">Cancel</span>
                  </button>
                  <button
                    type="button"
                    onClick={confirmDeleteProduct}
                    disabled={deleting}
                    className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="pointer-events-none">
                      {deleting ? (
                        <span className="inline-flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                          Deleting...
                        </span>
                      ) : (
                        'Delete'
                      )}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      <StorePaymentHistoryModal
        isOpen={showPaymentHistory}
        onClose={() => setShowPaymentHistory(false)}
        getAuthToken={getAuthToken}
      />

      {/* Sell to Student Modal */}
      <SellToStudentModal
        isOpen={!!sellProduct}
        onClose={() => setSellProduct(null)}
        product={sellProduct}
        studioId={studioUser?.studio_id ?? ''}
        getAuthToken={getAuthToken}
        onSaleComplete={() => fetchProducts({ silent: true })}
      />

      </div>
    </div>
  );
}
